import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { FaPhone } from "react-icons/fa";
import {
  getCountryNameFromCode,
  typeIcons,
  countryCodes,
  InlineEdit,
  InlineEditCheckbox,
  MONTH_LABELS,
  MONTH_OPTIONS,
  useFieldOptions,
  fieldOptionList,
  locationLabel,
  isValidEmail,
} from "../components/Utils";
import axios from "../axiosConfig";
import Modal from "../components/Modal";
import NotesTable from "../components/NotesTable";
import TasksTable from "../components/TasksTable";
import InteractionsTable from "../components/InteractionsTable";
import Table from "../components/Table";
import ContactSelector from "../components/ContactSelector";
import AccountSelector from "../components/AccountSelector";
import QuickEmail from "../components/QuickEmail";
import "./Account.css";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import Checkbox from '../components/Checkbox';
import { parseDbDate } from '../utils/dates';
import PageTitle from "../components/PageTitle";

const COUNTRY_OPTIONS_ORDER = ["GB", "US", "|", "..."];

// const SECTOR_OPTIONS = [
//   { value: 'Construction', label: 'Construction' },
//   { value: 'Engineering', label: 'Engineering' },
//   { value: 'Energy', label: 'Energy' },
//   { value: 'Financial Services', label: 'Financial Services' },
//   { value: 'Government', label: 'Government' },
//   { value: 'Healthcare', label: 'Healthcare' },
//   { value: 'Higher Education', label: 'Higher Education' },
//   { value: 'Hospitality', label: 'Hospitality' },
//   { value: 'Legal', label: 'Legal' },
//   { value: 'Manufacturing', label: 'Manufacturing' },
//   { value: 'Media', label: 'Media' },
//   { value: 'Non-profit', label: 'Non-profit' },
//   { value: 'Pharmaceutical', label: 'Pharmaceutical' },
//   { value: 'Professional Services', label: 'Professional Services' },
//   { value: 'Real Estate', label: 'Real Estate' },
//   { value: 'Retail', label: 'Retail' },
//   { value: 'Technology', label: 'Technology' },
//   { value: 'Telecommunications', label: 'Telecommunications' },
//   { value: 'Transportation', label: 'Transportation' },
//   { value: 'Utilities', label: 'Utilities' },
// ];

const Account = () => {
  const getDefaultEmployeeForm = () => ({
    title: "",
    first_name: "",
    last_name: "",
    office: "",
    job_title: "",
    email: "",
    office_phone: "",
    mobile_phone: "",
    tutor: false,
    attendee_only: false,
  });

  const [accountData, setAccountData] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [locationForm, setLocationForm] = useState({});
  const [locationSaving, setLocationSaving] = useState(false);
  const [locationDeleteConfirm, setLocationDeleteConfirm] = useState(false);
  const [locationToDelete, setLocationToDelete] = useState(null);
  const [locationEditId, setLocationEditId] = useState(null);

  const [employeeModalOpen, setEmployeeModalOpen] = useState(false);
  const [employeeMode, setEmployeeMode] = useState("select"); // 'select' or 'create'
  const [selectedContact, setSelectedContact] = useState(null);
  const [employeeValidationErrors, setEmployeeValidationErrors] = useState({});
  const [employeeForm, setEmployeeForm] = useState(getDefaultEmployeeForm);
  const [employeeSaving, setEmployeeSaving] = useState(false);
  const fieldOptions = useFieldOptions();

  const [accountJobs, setAccountJobs] = useState([]);
  const [accountJobsLoading, setAccountJobsLoading] = useState(true);

  const [departmentModalOpen, setDepartmentModalOpen] = useState(false);
  const [departmentAccount, setDepartmentAccount] = useState(null);
  const [departmentSaving, setDepartmentSaving] = useState(false);

  // References to table components for cross-refreshing
  const tasksTableRef = useRef(null);
  const interactionsTableRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();
  const id = new URLSearchParams(location.search).get("id");

  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem("user"));
    } catch {
      return null;
    }
  })();

  // Callback functions for cross-table refreshing
  const handleTaskCreated = () => {
    // When an interaction becomes a task, refresh tasks table
    if (tasksTableRef.current && tasksTableRef.current.fetchTasks) {
      tasksTableRef.current.fetchTasks();
    }
  };

  const handleInteractionCreated = () => {
    // When a task becomes an interaction, refresh interactions table
    if (
      interactionsTableRef.current &&
      interactionsTableRef.current.fetchInteractions
    ) {
      interactionsTableRef.current.fetchInteractions();
    }
  };

  const handleEmailSent = () => {
    // When an email is sent, refresh interactions table
    if (
      interactionsTableRef.current &&
      interactionsTableRef.current.fetchInteractions
    ) {
      interactionsTableRef.current.fetchInteractions();
    }
  };

  const formatJobDate = (dateStr) => {
    if (!dateStr) return "—";
    return parseDbDate(dateStr)?.toLocaleDateString("en-GB") ?? dateStr;
  };

  const jobColumns = [
    {
      key: "title",
      header: "Job Name",
      type: "text",
      width: 160,
    },
    {
      key: "stage",
      header: "Stage",
      type: "discrete",
      width: 96,
      render: (value) => value || "—",
    },
    {
      key: "job_type",
      header: "Job Type",
      type: "discrete",
      width: 96,
      render: (value) => value || "—",
    },
    {
      key: "start_date",
      header: "Start Date",
      type: "date",
      width: 96,
      render: (value) => formatJobDate(value),
    },
  ];

  const fetchAccountJobs = async () => {
    setAccountJobsLoading(true);
    try {
      const res = await axios.get(`/jobs/?account=${id}`);
      setAccountJobs(res.data || []);
    } catch (err) {
      console.error("Error fetching account jobs:", err);
    } finally {
      setAccountJobsLoading(false);
    }
  };

  const handleJobCreated = () => {
    fetchAccountJobs();
  };

  useEffect(() => {
    if (id) fetchAccountJobs();
  }, [id]);

  useEffect(() => {
    const fetchAccount = async () => {
      try {
        const res = await axios.get(`/accounts/${id}/`, {
          withCredentials: true,
        });
        const data = res.data;
        setAccountData(data);
        if (data.account) {
          const normalizeDateOnly = (v) => {
            if (!v) return "";
            if (typeof v === "string") return v.slice(0, 10);
            return "";
          };
          const acc = {
            ...data.account,
            parent: data.parent
              ? {
                  id: data.parent.id,
                  name_short: data.parent.name_short,
                }
              : null,
            procurement_intermediary_account:
              data.procurement_intermediary_account
                ? {
                    id: data.procurement_intermediary_account.id,
                    name_short:
                      data.procurement_intermediary_account.name_short,
                  }
                : null,
            supplier_platform_account: data.supplier_platform_account
              ? {
                  id: data.supplier_platform_account.id,
                  name_short: data.supplier_platform_account.name_short,
                }
              : null,
            invoicing_platform_account: data.invoicing_platform_account
              ? {
                  id: data.invoicing_platform_account.id,
                  name_short: data.invoicing_platform_account.name_short,
                }
              : null,
            primary_location: data.primary_location
              ? {
                  id: data.primary_location.id,
                  address_1: data.primary_location.address_1,
                  address_2: data.primary_location.address_2,
                  address_3: data.primary_location.address_3,
                  address_4: data.primary_location.address_4,
                  city: data.primary_location.city,
                  county: data.primary_location.county,
                  country: data.primary_location.country,
                  postcode: data.primary_location.postcode,
                  switchboard_number: data.primary_location.switchboard_number,
                }
              : null,
            default_invoice_contact: data.default_invoice_contact || null,
            default_invoice_location: data.default_invoice_location || null,
            temp_last_engagement: normalizeDateOnly(
              data.account.temp_last_engagement,
            ),
            temp_virtual: Boolean(data.account.temp_virtual),
            temp_in_person: Boolean(data.account.temp_in_person),
            temp_online_course: Boolean(data.account.temp_online_course),
            temp_public_course: Boolean(data.account.temp_public_course),
          };
          setAccount(acc);

          // Update page title
          const isStaging =
            (process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() ===
            "staging";
          document.title = `${acc.name_short} - FT Portal${isStaging ? " (Staging)" : ""}`;
        }
      } catch (err) {
        console.error("Error fetching account:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchAccount();
  }, [id]);

  // Reset page title on unmount
  useEffect(() => {
    const isStaging =
      (process.env.REACT_APP_ENVIRONMENT || "").toLowerCase() === "staging";
    return () => {
      document.title = isStaging ? "FT Portal (Staging)" : "FT Portal";
    };
  }, []);

  if (loading) return <p>Loading account...</p>;
  if (!accountData?.account) return <p>Account not found.</p>;

  const {
    employees = [],
    departments = [],
    locations = [],
    intermediary_locations: intermediaryLocations = [],
  } = accountData;

  // The default invoice contact/address can be the account's own or the
  // procurement intermediary's.
  const invoiceAccountIds = [
    account?.id,
    account?.procurement_intermediary_account?.id,
  ].filter(Boolean);
  const invoiceLocations = [...locations, ...intermediaryLocations];
  const invoiceLocationOptions = [
    ...locations.map((loc) => ({
      value: String(loc.id),
      label: locationLabel(loc),
    })),
    ...intermediaryLocations.map((loc) => ({
      value: String(loc.id),
      label: `${account?.procurement_intermediary_account?.name_short}: ${locationLabel(loc)}`,
    })),
  ];

  const defaultParticipants = [
    ...(user
      ? [
          {
            id: user.user_contact_id,
            type: "contact",
            first_name: user.first_name,
            last_name: user.last_name,
            is_sender: false,
          },
        ]
      : []),
    ...(account
      ? [
          {
            id: account.id,
            type: "account",
            name_short: account.name_short,
            is_sender: false,
          },
        ]
      : []),
  ];

  const openAddLocationModal = () => {
    setLocationEditId(null);
    setLocationForm({
      type: "",
      venue: false,
      address_1: "",
      address_2: "",
      address_3: "",
      address_4: "",
      city: "",
      county: "",
      country: "United Kingdom",
      postcode: "",
      switchboard_number: "",
      account_id: account.id,
    });
    setLocationModalOpen(true);
  };

  const openEditLocationModal = (loc) => {
    setLocationEditId(loc.id);
    setLocationForm({
      ...loc,
      account_id: account.id,
    });
    setLocationModalOpen(true);
  };

  const handleLocationFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    setLocationForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // Helper function to get country code from country name
  const getCountryCodeFromName = (countryName) => {
    const country = countryCodes.find((c) => c.name === countryName);
    return country ? country.code : "";
  };

  const getPhoneLink = (rawValue, countryName) => {
    const value = String(rawValue || "").trim();
    if (!value) return null;
    if (value.startsWith("+")) return { href: `tel:${value}`, label: value };

    const dialCode = getCountryCodeFromName(countryName);
    if (dialCode)
      return {
        href: `tel:+${dialCode}${value}`,
        label: `+${dialCode} ${value}`,
      };
    return { href: `tel:${value}`, label: value };
  };

  const getPhoneInputValue = (rawValue, countryName) => {
    const value = String(rawValue || "").trim();
    if (!value) return "";
    if (value.startsWith("+")) return value;

    const dialCode = getCountryCodeFromName(countryName);
    const digits = value.replace(/\D/g, "");
    if (!digits) return "";
    return dialCode ? `+${dialCode}${digits}` : digits;
  };

  const saveLocation = async () => {
    setLocationSaving(true);
    try {
      if (locationEditId) {
        await axios.patch(`/locations/${locationEditId}/`, locationForm, {
          withCredentials: true,
        });
      } else {
        await axios.post("/locations/", locationForm, {
          withCredentials: true,
        });
      }
      const res = await axios.get(`/accounts/${account.id}/`, {
        withCredentials: true,
      });
      setAccountData(res.data);
      setAccount((prev) => ({
        ...prev,
        default_invoice_location: res.data.default_invoice_location || null,
      }));
      if (locationEditId && locationEditId === account?.primary_location?.id) {
        setAccount((prev) => ({
          ...prev,
          primary_location: res.data.primary_location
            ? {
                id: res.data.primary_location.id,
                address_1: res.data.primary_location.address_1,
                address_2: res.data.primary_location.address_2,
                address_3: res.data.primary_location.address_3,
                address_4: res.data.primary_location.address_4,
                city: res.data.primary_location.city,
                county: res.data.primary_location.county,
                country: res.data.primary_location.country,
                postcode: res.data.primary_location.postcode,
                switchboard_number:
                  res.data.primary_location.switchboard_number,
              }
            : null,
        }));
      }
      setLocationModalOpen(false);
    } catch (err) {
      console.error("Error saving location:", err);
    } finally {
      setLocationSaving(false);
    }
  };

  const openDeleteLocationConfirm = (loc) => {
    setLocationToDelete(loc);
    setLocationDeleteConfirm(true);
  };

  const deleteLocation = async () => {
    if (!locationToDelete) return;
    try {
      await axios.delete(`/locations/${locationToDelete.id}/`, {
        withCredentials: true,
      });
      const res = await axios.get(`/accounts/${account.id}/`, {
        withCredentials: true,
      });
      setAccountData(res.data);
      // Deleting a location clears it as the default invoice location server-side.
      setAccount((prev) => ({
        ...prev,
        default_invoice_location: res.data.default_invoice_location || null,
      }));
      setLocationDeleteConfirm(false);
      setLocationToDelete(null);
    } catch (err) {
      console.error("Error deleting location:", err);
    }
  };

  const openAddEmployeeModal = (mode = "select") => {
    setEmployeeMode(mode);
    setSelectedContact(null);
    setEmployeeForm(getDefaultEmployeeForm());
    setEmployeeValidationErrors({});
    setEmployeeModalOpen(true);
  };

  const handleSelectedContactChange = (contact) => {
    setSelectedContact(contact);
    setEmployeeValidationErrors((prev) => {
      if (!prev.selectedContact) return prev;
      const next = { ...prev };
      delete next.selectedContact;
      return next;
    });
  };

  const handleEmployeeFormChange = (e) => {
    const { name, value, type, checked } = e.target;

    setEmployeeForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    setEmployeeValidationErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  };

  const handleEmployeeSubmit = async () => {
    const requiredErrors = {};

    // Validate form data for create mode
    if (employeeMode === "create") {
      if (!employeeForm.first_name.trim() || !employeeForm.last_name.trim()) {
        if (!employeeForm.first_name.trim()) requiredErrors.first_name = true;
        if (!employeeForm.last_name.trim()) requiredErrors.last_name = true;
      }
      if (employeeForm.email.trim() && !isValidEmail(employeeForm.email)) {
        alert("Please enter a valid email address");
        return;
      }
    } else if (employeeMode === "select" && !selectedContact) {
      requiredErrors.selectedContact = true;
    }

    if (Object.keys(requiredErrors).length > 0) {
      setEmployeeValidationErrors(requiredErrors);
      return;
    }

    setEmployeeValidationErrors({});

    setEmployeeSaving(true);
    try {
      if (employeeMode === "select" && selectedContact) {
        // Update existing contact's account (plus office/job title, if given)
        await axios.patch(
          `/contacts/${selectedContact.id}/`,
          {
            account_id: account.id,
            office: employeeForm.office || null,
            job_title: employeeForm.job_title || null,
          },
          { withCredentials: true },
        );
      } else if (employeeMode === "create") {
        // Create new contact with current account
        await axios.post(
          "/contacts/",
          {
            ...employeeForm,
            account_id: account.id,
            office: employeeForm.office || null,
          },
          { withCredentials: true },
        );
      }

      // Refresh account data to show updated employees
      const res = await axios.get(`/accounts/${account.id}/`, {
        withCredentials: true,
      });
      setAccountData(res.data);

      // Reset modal state
      setEmployeeModalOpen(false);
      setSelectedContact(null);
      setEmployeeForm(getDefaultEmployeeForm());
      setEmployeeMode("select");
      setEmployeeValidationErrors({});
    } catch (err) {
      console.error("Error adding employee:", err);
      alert("Error adding employee. Please try again.");
    } finally {
      setEmployeeSaving(false);
    }
  };

  const openAddDepartmentModal = () => {
    setDepartmentAccount(null);
    setDepartmentModalOpen(true);
  };

  const handleAddDepartmentConfirm = async () => {
    if (!departmentAccount) return;

    setDepartmentSaving(true);
    try {
      await axios.patch(
        `/accounts/${departmentAccount.id}/`,
        { parent_account_id: account.id },
        { withCredentials: true },
      );

      // Refresh account data to show the newly added department
      const res = await axios.get(`/accounts/${account.id}/`, {
        withCredentials: true,
      });
      setAccountData(res.data);

      setDepartmentModalOpen(false);
      setDepartmentAccount(null);
    } catch (err) {
      console.error("Error adding child account:", err);
      alert("Error adding child account. Please try again.");
    } finally {
      setDepartmentSaving(false);
    }
  };

  const handleInlineSave = async (field, newValue) => {
    try {
      let payload;
      let accountUpdate;

      const boolFields = [
        "temp_virtual",
        "temp_in_person",
        "temp_online_course",
        "temp_public_course",
      ];

      const codedFields = ["status", "category", "sales_channel"];

      const accountRefFields = [
        "procurement_intermediary_account",
        "supplier_platform_account",
        "invoicing_platform_account",
      ];

      if (field === "parent") {
        payload = { parent_account_id: newValue?.id || null };
        accountUpdate = { parent: newValue };
      } else if (accountRefFields.includes(field)) {
        payload = { [`${field}_id`]: newValue?.id || null };
        accountUpdate = { [field]: newValue };
      } else if (field === "primary_location") {
        const locId = newValue ? parseInt(newValue, 10) : null;
        payload = { primary_location: locId };
        const locObj = locations.find((l) => l.id === locId) || null;
        accountUpdate = { primary_location: locObj };
      } else if (field === "default_invoice_contact") {
        payload = { default_invoice_contact_id: newValue?.id || null };
        accountUpdate = { default_invoice_contact: newValue || null };
      } else if (field === "default_invoice_location") {
        const locId = newValue ? parseInt(newValue, 10) : null;
        payload = { default_invoice_location_id: locId };
        accountUpdate = {
          default_invoice_location:
            invoiceLocations.find((l) => l.id === locId) || null,
        };
      } else if (codedFields.includes(field)) {
        payload = { [field]: newValue || null };
        accountUpdate = { [field]: newValue || null };
      } else if (field === "results_month") {
        const month = newValue ? Number(newValue) : null;
        payload = { results_month: month };
        accountUpdate = { results_month: month };
      } else if (boolFields.includes(field)) {
        const boolVal =
          typeof newValue === "boolean" ? newValue : newValue === "yes";
        payload = { [field]: boolVal };
        accountUpdate = { [field]: boolVal };
      } else {
        payload = { [field]: newValue };
        accountUpdate = { [field]: newValue };
      }

      const res = await axios.patch(`/accounts/${account.id}/`, payload, {
        withCredentials: true,
      });
      if (res.data.success) {
        setAccount((prev) => ({ ...prev, ...accountUpdate }));
        if (field === "procurement_intermediary_account") {
          // Refresh the intermediary's locations offered as invoice addresses.
          const accRes = await axios.get(`/accounts/${account.id}/`, {
            withCredentials: true,
          });
          setAccountData(accRes.data);
        }
      }
    } catch (err) {
      console.error("Failed to update account field:", err);
    }
  };

  const handleSwitchboardSave = async (field, newValue) => {
    const locId = account?.primary_location?.id;
    if (!locId) return;
    try {
      const res = await axios.patch(
        `/locations/${locId}/`,
        { switchboard_number: newValue },
        {
          withCredentials: true,
        },
      );
      if (res.data.success) {
        setAccount((prev) => ({
          ...prev,
          primary_location: {
            ...prev.primary_location,
            switchboard_number: newValue,
          },
        }));
      }
    } catch (err) {
      console.error("Failed to update switchboard number:", err);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`/accounts/${account.id}/`, { withCredentials: true });
      navigate("/accounts");
    } catch (err) {
      console.error("Failed to delete account:", err);
    }
  };

  const handleNewBid = async () => {
    try {
      // Create a new job with the current account as account
      const response = await axios.post(
        "/jobs/",
        {
          account_id: account.id,
          ftl_contact_id: user?.user_contact_id || null,
        },
        { withCredentials: true },
      );

      if (response.data.success && response.data.job_id) {
        // Refresh jobs table before navigating
        handleJobCreated();
        // Navigate to the new job page
        navigate(`/job?id=${response.data.job_id}`);
      }
    } catch (err) {
      console.error("Failed to create new job:", err);
    }
  };

  return (
    <div className="page-layout">
      <PageTitle>
        {typeIcons["account"]} {account?.name_short}
      </PageTitle>
      <div className="page-header">
        <div className="page-header-buttons">
          <button onClick={handleNewBid}>New Bid</button>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="delete-btn"
          >
            Delete Account
          </button>
        </div>
      </div>

      <div className="page-content">
        <div>
          <div className="details-section">
            <h3>Account</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td>
                    <div className="details-label">Name</div>
                    <InlineEdit
                      field="name"
                      value={account?.name || ""}
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td>
                    <div className="details-label">Status</div>
                    <InlineEdit
                      field="status"
                      type="dropdown"
                      value={account?.status || ""}
                      options={fieldOptionList(fieldOptions, "accounts", "status")}
                      onSave={handleInlineSave}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Short Name</div>
                    <InlineEdit
                      field="name_short"
                      value={account?.name_short || ""}
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td>
                    <div className="details-label">Parent Account</div>
                    <InlineEdit
                      field="parent"
                      type="account"
                      value={account?.parent}
                      displayValue={
                        account?.parent?.id ? (
                          <Link to={`/account?id=${account.parent.id}`}>
                            {account.parent.name_short}
                          </Link>
                        ) : (
                          ""
                        )
                      }
                      onSave={handleInlineSave}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Long Name</div>
                    <InlineEdit
                      field="name_long"
                      value={account?.name_long || ""}
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td>
                    <div className="details-label">Sector</div>
                    <InlineEdit
                      field="sector"
                      type="dropdown"
                      value={account?.sector || ""}
                      options={fieldOptionList(fieldOptions, "accounts", "sector")}
                      onSave={handleInlineSave}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="details-section">
            <h3>Contact Details</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td>
                    <div className="details-label">Primary Location</div>
                    <InlineEdit
                      field="primary_location"
                      type="dropdown"
                      value={
                        account?.primary_location?.id
                          ? String(account.primary_location.id)
                          : ""
                      }
                      displayValue={locationLabel(account?.primary_location)}
                      options={locations.map((loc) => ({
                        value: String(loc.id),
                        label: locationLabel(loc),
                      }))}
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td>
                    <div className="details-label">Website</div>
                    <InlineEdit
                      field="website"
                      type="link"
                      value={account?.website || ""}
                      onSave={handleInlineSave}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Address</div>
                    <InlineEdit
                      field="address"
                      value={
                        account?.primary_location
                          ? [
                              account.primary_location.address_1,
                              account.primary_location.address_2,
                              account.primary_location.address_3,
                              account.primary_location.address_4,
                              account.primary_location.city,
                              account.primary_location.county,
                              account.primary_location.country,
                              account.primary_location.postcode,
                            ]
                              .filter(Boolean)
                              .join(", ")
                          : ""
                      }
                      placeholder="-"
                      disabled
                      disabled_reason="Change primary location address in locations table"
                    />
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Switchboard</div>
                    <InlineEdit
                      field="switchboard_number"
                      type="phone"
                      value={
                        account?.primary_location?.switchboard_number || ""
                      }
                      phoneCountry={account?.primary_location?.country}
                      placeholder="-"
                      onSave={handleSwitchboardSave}
                      disabled
                      disabled_reason="Change primary location switchboard in locations table"
                    />
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="details-section">
            <h3>Billing Information</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td>
                    <div className="details-label">Procurement Intermediary</div>
                    <InlineEdit
                      field="procurement_intermediary_account"
                      type="account"
                      value={account?.procurement_intermediary_account}
                      displayValue={
                        account?.procurement_intermediary_account?.id ? (
                          <Link
                            to={`/account?id=${account.procurement_intermediary_account.id}`}
                          >
                            {
                              account.procurement_intermediary_account
                                .name_short
                            }
                          </Link>
                        ) : (
                          ""
                        )
                      }
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td>
                    <div className="details-label">Supplier Platform</div>
                    <InlineEdit
                      field="supplier_platform_account"
                      type="account"
                      value={account?.supplier_platform_account}
                      displayValue={
                        account?.supplier_platform_account?.id ? (
                          <Link
                            to={`/account?id=${account.supplier_platform_account.id}`}
                          >
                            {account.supplier_platform_account.name_short}
                          </Link>
                        ) : (
                          ""
                        )
                      }
                      onSave={handleInlineSave}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Invoicing Platform</div>
                    <InlineEdit
                      field="invoicing_platform_account"
                      type="account"
                      value={account?.invoicing_platform_account}
                      displayValue={
                        account?.invoicing_platform_account?.id ? (
                          <Link
                            to={`/account?id=${account.invoicing_platform_account.id}`}
                          >
                            {account.invoicing_platform_account.name_short}
                          </Link>
                        ) : (
                          ""
                        )
                      }
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td>
                    <div className="details-label">Category</div>
                    <InlineEdit
                      field="category"
                      type="dropdown"
                      value={account?.category || ""}
                      options={fieldOptionList(fieldOptions, "accounts", "category")}
                      onSave={handleInlineSave}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Sales Channel</div>
                    <InlineEdit
                      field="sales_channel"
                      type="dropdown"
                      value={account?.sales_channel || ""}
                      options={fieldOptionList(fieldOptions, "accounts", "sales_channel")}
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td></td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Default Invoice Contact</div>
                    <InlineEdit
                      field="default_invoice_contact"
                      type="contact"
                      value={account?.default_invoice_contact}
                      contactAccountIds={invoiceAccountIds}
                      displayValue={
                        account?.default_invoice_contact?.id ? (
                          <span>
                            <Link
                              to={`/contact?id=${account.default_invoice_contact.id}`}
                            >
                              {account.default_invoice_contact.first_name}{" "}
                              {account.default_invoice_contact.last_name}
                            </Link>
                            {account.default_invoice_contact.email && (
                              <div className="details-subline">
                                {account.default_invoice_contact.email}
                              </div>
                            )}
                          </span>
                        ) : (
                          ""
                        )
                      }
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td>
                    <div className="details-label">Default Invoice Address</div>
                    <InlineEdit
                      field="default_invoice_location"
                      type="dropdown"
                      value={
                        account?.default_invoice_location?.id
                          ? String(account.default_invoice_location.id)
                          : ""
                      }
                      displayValue={
                        invoiceLocationOptions.find(
                          (o) =>
                            o.value ===
                            String(account?.default_invoice_location?.id),
                        )?.label ||
                        locationLabel(account?.default_invoice_location)
                      }
                      options={invoiceLocationOptions}
                      onSave={handleInlineSave}
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="details-section">
            <h3>Misc</h3>
            <table className="details-table">
              <tbody>
                <tr>
                  <td>
                    <div className="details-label">Last Job</div>
                    <InlineEdit
                      field="temp_last_engagement"
                      type="date"
                      value={account?.temp_last_engagement || ""}
                      onSave={handleInlineSave}
                    />
                    <div className="details-label" style={{ marginTop: 12 }}>
                      Last Interaction
                    </div>
                    <InlineEdit
                      field="last_interaction"
                      value={
                        parseDbDate(
                          accountData.last_interaction,
                        )?.toLocaleDateString("en-GB") ?? ""
                      }
                      disabled
                      disabled_reason="Date of the latest interaction not sent by FinanceTalking"
                    />
                  </td>
                  <td>
                    <div className="details-label">Training type received</div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 6,
                      }}
                    >
                      <InlineEditCheckbox
                        field="temp_in_person"
                        label="In-Person"
                        value={!!account?.temp_in_person}
                        onSave={handleInlineSave}
                      />
                      <InlineEditCheckbox
                        field="temp_virtual"
                        label="Virtual"
                        value={!!account?.temp_virtual}
                        onSave={handleInlineSave}
                      />
                      <InlineEditCheckbox
                        field="temp_online_course"
                        label="Online Course"
                        value={!!account?.temp_online_course}
                        onSave={handleInlineSave}
                      />
                      <InlineEditCheckbox
                        field="temp_public_course"
                        label="Public Course"
                        value={!!account?.temp_public_course}
                        onSave={handleInlineSave}
                      />
                    </div>
                  </td>
                </tr>
                <tr>
                  <td>
                    <div className="details-label">Results Month</div>
                    <InlineEdit
                      field="results_month"
                      type="dropdown"
                      value={
                        account?.results_month
                          ? String(account.results_month)
                          : ""
                      }
                      displayValue={MONTH_LABELS[account?.results_month] || ""}
                      options={MONTH_OPTIONS}
                      onSave={handleInlineSave}
                    />
                  </td>
                  <td></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <h3>Notes</h3>
        <NotesTable accountId={account.id} accountName={account.name_short} />

        <hr />

        {/* Child Accounts */}
        <div>
          <div className="section-header">
            <h3>Child Accounts</h3>
          </div>
          <button onClick={openAddDepartmentModal}>+ Child Account</button>
          <Table
            data={departments}
            columns={[
              {
                key: "name_short",
                header: "Name",
                type: "static",
                sortable: false,
                width: 128,
                render: (value, row) => (
                  <Link to={`/account?id=${row.id}`}>{value}</Link>
                ),
              },
              {
                key: "website",
                header: "Website",
                type: "static",
                sortable: false,
                width: 128,
                render: (value) =>
                  value ? (
                    <a href={value} target="_blank" rel="noopener noreferrer">
                      {value}
                    </a>
                  ) : (
                    "-"
                  ),
              },
            ]}
            loading={false}
            emptyMessage="No child accounts found under this account."
          />
        </div>

        <hr />

        {/* Locations */}
        <div>
          <div className="section-header">
            <h3>Locations</h3>
          </div>
          <button onClick={openAddLocationModal}>+ Add Location</button>
          <Table
            data={locations}
            columns={[
              {
                key: "type",
                header: "Address Type",
                type: "static",
                sortable: false,
                width: 80,
                render: (value) => value || "-",
              },
              {
                key: "venue",
                header: "Venue",
                type: "static",
                sortable: false,
                width: 64,
                render: (value) => (value ? "Yes" : "No"),
              },
              {
                key: "address",
                header: "Address",
                type: "static",
                sortable: false,
                width: 160,
                render: (value, row) =>
                  [row.address_1, row.address_2, row.address_3, row.address_4]
                    .filter(Boolean)
                    .join(", ") || "-",
              },
              {
                key: "city",
                header: "City",
                type: "static",
                sortable: false,
                width: 80,
              },
              {
                key: "county",
                header: "County",
                type: "static",
                sortable: false,
                width: 80,
              },
              {
                key: "country",
                header: "Country",
                type: "static",
                sortable: false,
                width: 80,
              },
              {
                key: "postcode",
                header: "Postcode",
                type: "static",
                sortable: false,
                width: 64,
              },
              {
                key: "switchboard",
                header: "Switchboard",
                type: "static",
                sortable: false,
                width: 96,
                render: (value, row) => {
                  const link = getPhoneLink(
                    row.switchboard_number,
                    row.country,
                  );
                  if (!link) return "-";
                  return (
                    <a href={link.href}>
                      <FaPhone /> {link.label}
                    </a>
                  );
                },
              },
              {
                key: "actions",
                header: "Actions",
                type: "static",
                sortable: false,
                width: 96,
                render: (value, row) => (
                  <div>
                    <button onClick={() => openEditLocationModal(row)}>
                      Edit
                    </button>
                    <button
                      onClick={() => openDeleteLocationConfirm(row)}
                      className="delete-btn"
                    >
                      Delete
                    </button>
                  </div>
                ),
              },
            ]}
            loading={false}
            emptyMessage="No locations found for this account."
          />
        </div>

        <hr />

        {/* Employees */}
        <div>
          <div className="section-header">
            <h3>Employees</h3>
          </div>
          <button onClick={() => openAddEmployeeModal("create")}>
            + Create New Employee Contact
          </button>
          <button onClick={() => openAddEmployeeModal("select")}>
            + Add Existing Contact
          </button>
          <Table
            data={employees}
            columns={[
              {
                key: "name",
                header: "Name",
                type: "static",
                sortable: false,
                width: 128,
                render: (value, row) => (
                  <Link to={`/contact?id=${row.id}`}>
                    {row.title} {row.first_name} {row.last_name}
                  </Link>
                ),
              },
              {
                key: "job_title",
                header: "Job Title",
                type: "static",
                sortable: false,
                width: 128,
              },
              {
                key: "email",
                header: "Email",
                type: "static",
                sortable: false,
                width: 112,
                render: (value, row) => (
                  <QuickEmail
                    initialParticipants={
                      row.email
                        ? [
                            {
                              id: row.id,
                              type: "contact",
                              first_name: row.first_name,
                              last_name: row.last_name,
                              email: row.email,
                              is_sender: false,
                            },
                          ]
                        : []
                    }
                    onEmailSent={handleEmailSent}
                  />
                ),
              },
              {
                key: "mobile_phone",
                header: "Mobile Phone",
                type: "static",
                sortable: false,
                width: 96,
                render: (value) => {
                  const link = getPhoneLink(value);
                  if (!link) return "-";
                  return (
                    <a href={link.href}>
                      <FaPhone /> {link.label}
                    </a>
                  );
                },
              },
              {
                key: "office_phone",
                header: "Office Phone",
                type: "static",
                sortable: false,
                width: 96,
                render: (value) => {
                  const link = getPhoneLink(value);
                  if (!link) return "-";
                  return (
                    <a href={link.href}>
                      <FaPhone /> {link.label}
                    </a>
                  );
                },
              },
            ]}
            loading={false}
            emptyMessage="No employees found for this account."
          />
        </div>

        <hr />

        {/* Jobs */}
        <div>
          <div className="section-header">
            <h3>Jobs</h3>
          </div>
          <Table
            data={accountJobs}
            columns={jobColumns}
            loading={accountJobsLoading}
            emptyMessage="No jobs found."
            onRowClick={(job) => (window.location.href = `/job?id=${job.id}`)}
            defaultSortColumn="start_date"
            defaultSortDirection="desc"
          />
        </div>

        <hr />

        <h3>Tasks</h3>
        <TasksTable
          accountId={account.id}
          defaultParticipants={defaultParticipants}
          onInteractionCreated={handleInteractionCreated}
          ref={tasksTableRef}
        />

        <h3>Interactions</h3>
        <InteractionsTable
          accountId={account.id}
          defaultParticipants={defaultParticipants}
          onTaskCreated={handleTaskCreated}
          ref={interactionsTableRef}
        />
      </div>

      {/* Delete Account Confirmation Modal */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        title="Confirm Delete"
      >
        <p>
          Are you sure you want to delete this account? This action cannot be
          undone.
        </p>
        <div>
          <button onClick={handleDelete} style={{ color: "red" }}>
            Yes, Delete
          </button>
          <button onClick={() => setShowDeleteConfirm(false)}>Cancel</button>
        </div>
      </Modal>

      {/* Add Child Account Modal */}
      <Modal
        isOpen={departmentModalOpen}
        onClose={() => setDepartmentModalOpen(false)}
        title="Add Child Account"
      >
        <label>
          Account:
          <AccountSelector
            value={departmentAccount}
            onChange={setDepartmentAccount}
          />
        </label>
        <div>
          <button
            onClick={handleAddDepartmentConfirm}
            disabled={!departmentAccount || departmentSaving}
          >
            {departmentSaving ? "Saving..." : "Confirm"}
          </button>
          <button type="button" onClick={() => setDepartmentModalOpen(false)}>
            Cancel
          </button>
        </div>
      </Modal>

      {/* Add/Edit Location Modal */}
      <Modal
        isOpen={locationModalOpen}
        onClose={() => setLocationModalOpen(false)}
        title={locationEditId ? "Edit Location" : "Add Location"}
      >
        <form
          className="form"
          onSubmit={(e) => {
            e.preventDefault();
            saveLocation();
          }}
        >
          {!locationEditId && (
            <label>
              Copy existing location:
              <select
                autoComplete="off"
                value=""
                onChange={(e) => {
                  const sourceId = e.target.value;
                  if (!sourceId) return;
                  const source = accountData?.locations?.find(
                    (loc) => String(loc.id) === sourceId,
                  );
                  if (!source) return;
                  setLocationForm((prev) => ({
                    ...prev,
                    type: source.type || "",
                    venue: !!source.venue,
                    address_1: source.address_1 || "",
                    address_2: source.address_2 || "",
                    address_3: source.address_3 || "",
                    address_4: source.address_4 || "",
                    city: source.city || "",
                    county: source.county || "",
                    country: source.country || prev.country,
                    postcode: source.postcode || "",
                    switchboard_number: source.switchboard_number || "",
                  }));
                }}
              >
                <option value="">Select a location...</option>
                {accountData?.locations?.map((loc) => (
                  <option key={loc.id} value={loc.id}>
                    {locationLabel(loc)}
                  </option>
                ))}
              </select>
            </label>
          )}
          <label>
            Address Type:
            <select
              autoComplete="off"
              name="type"
              value={locationForm.type || ""}
              onChange={handleLocationFormChange}
            >
              <option value="">Select type...</option>
              {fieldOptionList(fieldOptions, "locations", "type").map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <div className="checkbox-group">
            <label>
              Available as a venue for other accounts
              <Checkbox
                name="venue"
                checked={!!locationForm.venue}
                onChange={handleLocationFormChange}
              />
            </label>
          </div>
          <label>
            Address 1:
            <input
              autoComplete="off"
              type="text"
              name="address_1"
              value={locationForm.address_1 || ""}
              onChange={handleLocationFormChange}
              required
            />
          </label>
          <label>
            Address 2:
            <input
              autoComplete="off"
              type="text"
              name="address_2"
              value={locationForm.address_2 || ""}
              onChange={handleLocationFormChange}
            />
          </label>
          <label>
            Address 3:
            <input
              autoComplete="off"
              type="text"
              name="address_3"
              value={locationForm.address_3 || ""}
              onChange={handleLocationFormChange}
            />
          </label>
          <label>
            Address 4:
            <input
              autoComplete="off"
              type="text"
              name="address_4"
              value={locationForm.address_4 || ""}
              onChange={handleLocationFormChange}
            />
          </label>
          <label>
            City:
            <input
              autoComplete="off"
              type="text"
              name="city"
              value={locationForm.city || ""}
              onChange={handleLocationFormChange}
            />
          </label>
          <label>
            County:
            <input
              autoComplete="off"
              type="text"
              name="county"
              value={locationForm.county || ""}
              onChange={handleLocationFormChange}
            />
          </label>
          <label>
            Country:
            <select
              autoComplete="off"
              name="country"
              value={locationForm.country || ""}
              onChange={handleLocationFormChange}
              required
            >
              <option value="">Select country...</option>
              {countryCodes.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name}
                </option>
              ))}
            </select>
          </label>
          <label>
            Postcode:
            <input
              autoComplete="off"
              type="text"
              name="postcode"
              value={locationForm.postcode || ""}
              onChange={handleLocationFormChange}
            />
          </label>
          <label>
            Switchboard Number:
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <PhoneInput
                international
                name="switchboard_number"
                countryOptionsOrder={COUNTRY_OPTIONS_ORDER}
                value={getPhoneInputValue(
                  locationForm.switchboard_number,
                  locationForm.country,
                )}
                onChange={(value) => {
                  setLocationForm((prev) => ({
                    ...prev,
                    switchboard_number: value || "",
                  }));
                }}
                placeholder="Enter phone number"
                style={{ flex: 1 }}
              />
            </div>
          </label>
          <div>
            <button type="submit" disabled={locationSaving}>
              {locationSaving ? "Saving..." : "Save"}
            </button>
            <button type="button" onClick={() => setLocationModalOpen(false)}>
              Cancel
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete Location Confirmation Modal */}
      <Modal
        isOpen={locationDeleteConfirm}
        onClose={() => setLocationDeleteConfirm(false)}
        title="Confirm Delete Location"
      >
        <p>
          Are you sure you want to delete the location{" "}
          <strong>{locationLabel(locationToDelete)}</strong>? This action cannot be
          undone.
        </p>
        <div>
          <button onClick={deleteLocation} style={{ color: "red" }}>
            Yes, Delete
          </button>
          <button onClick={() => setLocationDeleteConfirm(false)}>
            Cancel
          </button>
        </div>
      </Modal>

      {/* Add Employee Modal */}
      <Modal
        isOpen={employeeModalOpen}
        onClose={() => {
          setEmployeeModalOpen(false);
          setSelectedContact(null);
          setEmployeeMode("select");
          setEmployeeValidationErrors({});
        }}
        title={
          employeeMode === "select"
            ? "Add Existing Contact"
            : "Create New Employee Contact"
        }
      >
        {employeeMode === "select" ? (
          <div className="form">
            <label>
              Select Contact <em>(required)</em>:
              <div
                className={
                  employeeValidationErrors.selectedContact
                    ? "required-invalid"
                    : ""
                }
              >
                <ContactSelector
                  value={selectedContact}
                  onChange={handleSelectedContactChange}
                />
              </div>
            </label>

            <label>
              Location:
              <select
                autoComplete="off"
                name="office"
                value={employeeForm.office || ""}
                onChange={handleEmployeeFormChange}
                disabled={accountData?.locations?.length === 0}
              >
                {accountData?.locations?.length === 0 ? (
                  <option value="">No locations available</option>
                ) : (
                  <>
                    <option value="">Select one...</option>
                    {accountData?.locations?.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {locationLabel(loc)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </label>

            <label>
              Job Title:
              <input
                autoComplete="off"
                type="text"
                name="job_title"
                value={employeeForm.job_title}
                onChange={handleEmployeeFormChange}
              />
            </label>

            <div>
              <button
                onClick={handleEmployeeSubmit}
                disabled={employeeSaving}
                type="button"
              >
                {employeeSaving ? "Adding..." : "Add Contact"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEmployeeModalOpen(false);
                  setSelectedContact(null);
                  setEmployeeMode("select");
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <form
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleEmployeeSubmit();
            }}
          >
            <label>
              Title:
              <input
                autoComplete="off"
                type="text"
                name="title"
                value={employeeForm.title}
                onChange={handleEmployeeFormChange}
              />
            </label>

            <label>
              First Name <em>(required)</em>:
              <input
                autoComplete="off"
                type="text"
                name="first_name"
                value={employeeForm.first_name}
                onChange={handleEmployeeFormChange}
                className={
                  employeeValidationErrors.first_name ? "required-invalid" : ""
                }
              />
            </label>

            <label>
              Last Name <em>(required)</em>:
              <input
                autoComplete="off"
                type="text"
                name="last_name"
                value={employeeForm.last_name}
                onChange={handleEmployeeFormChange}
                className={
                  employeeValidationErrors.last_name ? "required-invalid" : ""
                }
              />
            </label>

            <label>
              Location:
              <select
                autoComplete="off"
                name="office"
                value={employeeForm.office || ""}
                onChange={handleEmployeeFormChange}
                disabled={accountData?.locations?.length === 0}
              >
                {accountData?.locations?.length === 0 ? (
                  <option value="">No locations available</option>
                ) : (
                  <>
                    <option value="">Select one...</option>
                    {accountData?.locations?.map((loc) => (
                      <option key={loc.id} value={loc.id}>
                        {locationLabel(loc)}
                      </option>
                    ))}
                  </>
                )}
              </select>
            </label>

            <label>
              Job Title:
              <input
                autoComplete="off"
                type="text"
                name="job_title"
                value={employeeForm.job_title}
                onChange={handleEmployeeFormChange}
              />
            </label>

            <label>
              Email:
              <input
                autoComplete="off"
                type="email"
                name="email"
                value={employeeForm.email}
                onChange={handleEmployeeFormChange}
              />
            </label>

            <label>
              Office Phone:
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <PhoneInput
                  international
                  name="office_phone"
                  countryOptionsOrder={COUNTRY_OPTIONS_ORDER}
                  value={getPhoneInputValue(employeeForm.office_phone)}
                  onChange={(value) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      office_phone: value || "",
                    }))
                  }
                  placeholder="Enter phone number"
                  style={{ flex: 1 }}
                />
              </div>
            </label>

            <label>
              Mobile Phone:
              <div
                style={{ display: "flex", alignItems: "center", gap: "8px" }}
              >
                <PhoneInput
                  international
                  name="mobile_phone"
                  countryOptionsOrder={COUNTRY_OPTIONS_ORDER}
                  value={getPhoneInputValue(employeeForm.mobile_phone)}
                  onChange={(value) =>
                    setEmployeeForm((prev) => ({
                      ...prev,
                      mobile_phone: value || "",
                    }))
                  }
                  placeholder="Enter phone number"
                  style={{ flex: 1 }}
                />
              </div>
            </label>

            <div className="checkbox-group">
              <label>
                Tutor
                <Checkbox
                  name="tutor"
                  checked={employeeForm.tutor}
                  onChange={handleEmployeeFormChange}
                />
              </label>
            </div>

            <div className="checkbox-group">
              <label>
                Attendee Only
                <Checkbox
                  name="attendee_only"
                  checked={employeeForm.attendee_only}
                  onChange={handleEmployeeFormChange}
                />
              </label>
            </div>

            <div>
              <button type="submit" disabled={employeeSaving}>
                {employeeSaving ? "Creating..." : "Create Employee"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEmployeeModalOpen(false);
                  setSelectedContact(null);
                  setEmployeeMode("select");
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
    </div>
  );
};

export default Account;
